import React, { Component } from 'react';
import { Row, Col } from 'react-bootstrap';

import {
  Text,
  Button,
  Modal,
  Icon,
  Flex,
} from 'react-orange-ui';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';

import PasswordInput from '../../../orange-ui/components/Input/PasswordInput';
import CnilContainer from './CnilContainer';
import LabelWithHelp from '../../Title/LabelWithHelp';

import WordingService from '../../../services/WordingService';
import TagService from '../../../services/TagService';

const iconSize = 48;

const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const RotateIcon = styled.div`
  display: inline-block;
  height: ${iconSize}px;
  width: ${iconSize}px;
  animation: ${rotate360} 2s linear infinite;
`;

const StyledModal = styled(Modal)`
  position: absolute;
  z-index: 10;
`;

class ResetView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showCnil: false,
      value: '',
      valueConfirm: '',
      passAgainBlurred: false,
    }
    this.passwordInputRef = React.createRef();
    this.confirmPasswordInputRef = React.createRef();
  };

  /**
   * Initialize the password validator when the component is mounted.
   */
  componentDidMount() {
    const { initPasswordValidator } = this.props;
    initPasswordValidator();
  }

  /**
   * Focus input when receive submit error
   * @param {Object} prevProps 
   */
  componentDidUpdate(prevProps) {
    const { submitError } = this.props;

    if (prevProps.submitError !== submitError) {
      if (submitError) {
        this.passwordInputRef.current.focusTextInput();
      }
    }
  }

  /**
   * Validate password
   */
  validatePassword = () => {
    this.checkPasswordIsLogin();
    this.checkPasswordMatching();
  };

  /**
   * Check the passwords are matching
   */
  checkPasswordMatching = () => {
    const { value, valueConfirm, passAgainBlurred } = this.state;
    const { setInputError, clearInputError } = this.props;

    if (passAgainBlurred && valueConfirm) {
      if (value !== valueConfirm) {
        setInputError({
          field: 'passAgain',
          error: 'notEqual',
        });
      } else {
        clearInputError({ field: 'passAgain' });
      }
    }
  };

  /**
   * Check if the password match the login
   */
  checkPasswordIsLogin = () => {
    const { value } = this.state;
    const { login, setCnilError } = this.props;

    if (login && (value.toLowerCase() === login.toLowerCase())) {
      setCnilError('isLogin');
    }
  };

  /**
   * Update CNIL rules and validate password on change
   * @param value
   */
  handleChangePassword = (value) => {
    const { updateCnil, clearInputError } = this.props;

    clearInputError({ field: 'password' });
    updateCnil(value);
    this.setState({
      value,
    }, () => {
      this.validatePassword();
    });
  };

  /**
   * Validate password on change
   * @param valueConfirm
   */
  handleChangePassAgain = (valueConfirm) => {
    this.setState({
      valueConfirm,
    }, () => {
      this.validatePassword();
    });
  };

  /**
   * Show CNIL rules on focus
   */
  handleFocus = () => {
    const { showCnil } = this.state;

    if (!showCnil) {
      this.setState({
        showCnil: true,
      });
    }
  };

  handleBlur = (e) => {
    e.preventDefault();
    const { value } = this.state;
    const { setInputError } = this.props;

    if (!value) {
      setInputError({ field: 'password', error: 'empty' });
    }
  };

  handleBlurPassAgain = (e) => {
    e.preventDefault();
    const { valueConfirm, passAgainBlurred } = this.state;
    const { setInputError } = this.props;

    if (!valueConfirm) {
      setInputError({ field: 'passAgain', error: 'empty' });
    }
    if (!passAgainBlurred) {
      this.setState({ passAgainBlurred: true }, () => this.validatePassword());
    }
  };

  /**
   * Submit the new password
   * @param e
   */
  handleSubmit = (e) => {
    e.preventDefault();
    const { value, valueConfirm } = this.state;
    const { resetPassword } = this.props;

    resetPassword({
      password: value,
      passAgain: valueConfirm,
    });
  };

  render() {
    const {
      showCnil, value, valueConfirm,
    } = this.state;
    const {
      cnilRules,
      cnilError,
      isSaving,
      isLoading,
      passwordError,
      passAgainError,
    } = this.props;

    const isEqual = value === valueConfirm;
    const modalWidth = isMobile ? '90%' : '50%';

    return (
      <form id="update-password-form" onSubmit={this.handleSubmit}>
        <Row>
          <Col xs={12} sm={12} md={9} lg={6} xl={6}>
            <div>
              <LabelWithHelp
                id="reset-password-label"
                help={WordingService.getWording('caption', 'changePasswordHelping')}
                htmlFor="new-password"
              >
                {WordingService.getWording('caption', 'createNewPassword')}
              </LabelWithHelp>
              <PasswordInput
                ref={this.passwordInputRef}
                id="new-password"
                name="new-password"
                size="m"
                maxLenght={36}
                mandatory
                onChange={this.handleChangePassword}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onEyeIconClick={() => TagService.sendAction('showPassword')}
                value={value}
                valid={!(passwordError || cnilError)}
                errorMessage={passwordError}
                errorWithIcon={true}
                aria-describedby="cnil-rules"
              />
              {showCnil && <CnilContainer cnilRules={cnilRules} cnilError={cnilError} />}
              <br />
              <Text as="label" size="l" bold id="confirm-password-label" htmlFor="confirm-password" style={{ marginBottom: 8 }}>
                {WordingService.getWording('caption', 'confirmNewPassword')}
              </Text>
              <PasswordInput
                ref={this.confirmPasswordInputRef}
                id="confirm-password"
                name="confirm-password"
                size="m"
                onChange={this.handleChangePassAgain}
                onBlur={this.handleBlurPassAgain}
                onEyeIconClick={() => TagService.sendAction('showPasswordConfirm')}
                maxLenght={36}
                mandatory
                value={valueConfirm}
                valid={!passAgainError}
                errorMessage={passAgainError}
                errorWithIcon={true}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <br />
            <Button id="button-validate-password" htmlType="submit" type="important" disabled={!isEqual || isLoading || !cnilRules.result}>
              {WordingService.getWording('button', 'validate')}
            </Button>
          </Col>
        </Row>
        <StyledModal isOpen={isSaving} width={modalWidth} overlay={{ transition: { type: 'linear', time: 0.5 }, shouldCloseOnClick: false, shouldDisplay: true }}>
          <Flex alignItems="center" justifyContent="center" flexDirection="column">
            <RotateIcon>
              <Icon color="orange" width={iconSize} height={iconSize} icon="Syncronise" />
            </RotateIcon>
            <Text bold size="l" tabIndex={0}>
              {WordingService.getWording('caption', 'updatingPassword')}
            </Text>
          </Flex>
        </StyledModal>
      </form>
    );
  }
}

ResetView.defaultProps = {
  cnilRules: {
    result: false,
  },
  cnilError: null,
  isSaving: false,
  isLoading: false,
  passwordError: null,
  passAgainError: null,
  submitError: false,
};

ResetView.propTypes = {
  /** Function to initialize password validator rules */
  initPasswordValidator: PropTypes.func.isRequired,
  /** Function to validate CNIL rules */
  updateCnil: PropTypes.func.isRequired,
  /** Function to set a specific field error */
  setInputError: PropTypes.func.isRequired,
  /** Function to set a specific CNIL error */
  setCnilError: PropTypes.func.isRequired,
  /** Function to reset password */
  resetPassword: PropTypes.func.isRequired,
  /** Function to clear input error */
  clearInputError: PropTypes.func.isRequired,
  /** Object CNIL password validator rules */
  cnilRules: PropTypes.shape({
    result: PropTypes.bool,
  }),
  /** CNIL first error */
  cnilError: PropTypes.string,
  /** Boolean indicate if the password is saving */
  isSaving: PropTypes.bool,
  /** Boolean indicate if a request is currently loading */
  isLoading: PropTypes.bool,
  login: PropTypes.string.isRequired,
  passwordError: PropTypes.string,
  passAgainError: PropTypes.string,
  submitError: PropTypes.string,
};

export default ResetView;
